import { deg2rad } from "../math/angles";
import type { Section } from "../geometry/section";
import { rcFrame, rcStemThicknessAt } from "../geometry/rcCantilever";
import { buildSegments } from "../pressure/diagram";
import { hydrodynamic } from "../pressure/water";
import type { CaseParams } from "../loads/assemble";
import type { RcCantileverGeometry, WallInput } from "../types/input";
import type { WarningCode } from "../warnings";
import { factored, soilColumnWeight, upliftAt, type ClassSums, type DesignCase } from "./cantilever";

/**
 * Payandalı duvar (Huntington / Bowles yaklaşımı):
 *  - Gövde: payandalar arasında yatay sürekli plak; mesnette M⁻ = p·L²/12, açıklıkta M⁺ = p·L²/16, V = p·L/2 (L = temiz açıklık).
 *    Taban yakınındaki alt şerit (yükseklik a = min(L/4, h_s)) düşey konsol: M_v ≈ p_taban·a²/2.
 *  - Topuk: payandalara asılı sürekli plak (net aşağı yük w = plak + dolgu + sürşarj − q_d); mesnette üst, açıklıkta alt donatı.
 *  - Payanda: taban kesitinde T kiriş konsol; M = M_gövde,konsol·s, V = H_gövde·s; etkin derinlik d = L_topuk·sinα − pas payı (α eğik kenarın yatayla açısı);
 *    eğik çekme donatısı kesmeyi azaltır: V_eff = V − (M/d)·tanβ (β: eğik kenarın düşeyle açısı).
 *  - Bağlantı donatıları: gövde–payanda yatay çekme T_h = p·s; topuk–payanda düşey çekme T_v = w·s.
 */

export interface StemPressure { y: number; p: ClassSums }

/** Gövde arkasındaki düşey düzlemde y kotundaki yatay basınç bileşenleri (kN/m²), sınıflara ayrılmış. */
export function stemPressureAt(input: WallInput, g: RcCantileverGeometry, section: Section, cp: CaseParams, y: number, warn: (c: WarningCode) => void): ClassSums {
  const f = rcFrame(g);
  const isSeismic = cp.id !== "static";
  const kh = isSeismic ? cp.kh : 0, kvF = isSeismic ? cp.kvFactor : 1;
  const yC = section.yTop;
  const p: ClassSums = { G: 0, Q: 0, H: 0, F: 0, E: 0 };
  if (y >= yC - 1e-9) return p;
  const plane = { psi: Math.PI / 2, yC, yBot: f.tf };
  const segsQ = buildSegments({ section, backfill: input.backfill, water: input.water, surcharge: input.loads.surcharge, kh, kvFactor: kvF, kvOnTotal: input.seismic.kvOnTotalPressure === true, warn, plane });
  const segs0 = input.loads.surcharge > 0 ? buildSegments({ section, backfill: input.backfill, water: input.water, surcharge: 0, kh, kvFactor: kvF, kvOnTotal: input.seismic.kvOnTotalPressure === true, warn, plane }) : segsQ;
  const z = yC - y;
  const at = (segs: typeof segsQ): { st: number; dyn: number } => {
    const s = segs.find((g) => z >= g.zTop - 1e-9 && z <= g.zBot + 1e-9) ?? segs[segs.length - 1]!;
    const sigmaV = s.sigmaVTop + s.gammaEff * (z - s.zTop);
    const Kac = 2 * Math.sqrt(s.Kstatic);
    return { st: Math.max(0, s.Kstatic * sigmaV - s.c * Kac) * Math.cos(s.delta), dyn: Math.max(0, s.Kdyn * kvF * sigmaV) * Math.cos(s.delta) };
  };
  const q = at(segsQ), q0 = at(segs0);
  p.H += q0.st; p.Q += q.st - q0.st; p.E += q.dyn;
  const hw = input.water.backLevel;
  if (hw !== null && hw > y) {
    p.F += input.water.gammaWater * (hw - y);
    if (isSeismic && input.backfill.permeability === "permeable") {
      // Hidrodinamik basınç dağılımı: toplam (7/12)·khw·γw·h² → parabolik; yaklaşık üçgen 2·F/h·(1 − z/h) yerine düzgün eşdeğer kullanılır
      const hd = hydrodynamic(hw - f.tf, input.water.gammaWater, cp.khWater);
      p.E += hd.F / Math.max(hw - f.tf, 1e-9);
    }
  }
  if (isSeismic && input.seismic.includeWallInertia && kh > 0) p.E += kh * input.material.gamma * rcStemThicknessAt(g, y);
  return p;
}

export interface CounterfortGeometry { s: number; t: number; L: number; alpha: number; beta: number; area: number; centroid: { x: number; y: number } }
export function counterfortGeometry(g: RcCantileverGeometry): CounterfortGeometry | null {
  if (!g.counterforts) return null;
  const f = rcFrame(g);
  const s = g.counterforts.spacing, t = g.counterforts.thickness, bTop = g.counterforts.topWidth;
  const L = Math.max(0.1, s - t);
  const alpha = Math.atan2(f.hs, Math.max(1e-6, f.heel - bTop));   // eğik kenar: (B, tf) → (xTopBack + bTop, yTop)
  const beta = Math.PI / 2 - alpha;
  // Profil: (xStemBack, tf), (B, tf), (xTopBack + bTop, yTop), (xTopBack, yTop) — yamuk (bTop = 0 → üçgen)
  const a1 = f.heel, a2 = bTop;
  const area = ((a1 + a2) / 2) * f.hs;
  const cy = f.tf + (f.hs * (a1 + 2 * a2)) / (3 * (a1 + a2));
  const cx = f.xStemBack + (a1 * a1 + a1 * a2 + a2 * a2) / (3 * (a1 + a2));
  return { s, t, L, alpha, beta, area, centroid: { x: cx, y: cy } };
}

export interface SlabStrip { y: number; p: number; Msupport: number; Mspan: number; V: number }
/** Gövde plağı: her kotta faktörlü basınç ve sürekli plak momentleri (kNm/m yükseklik) */
export function stemSlabStrips(input: WallInput, g: RcCantileverGeometry, section: Section, cases: CaseParams[], dcs: DesignCase[], levels: number[], warn: (c: WarningCode) => void): SlabStrip[] {
  const cf = counterfortGeometry(g)!;
  return levels.map((y) => {
    let p = 0;
    for (const dc of dcs) {
      const cp = cases.find((c) => c.id === dc.caseId)!;
      p = Math.max(p, factored(stemPressureAt(input, g, section, cp, y, warn), dc.combo));
    }
    return { y, p, Msupport: (p * cf.L * cf.L) / 12, Mspan: (p * cf.L * cf.L) / 16, V: (p * cf.L) / 2 };
  });
}

/** Topuk plağı: en büyük faktörlü net aşağı yük (kN/m²) ve sürekli plak momentleri; net yük topuk boyunca en elverişsiz noktada */
export function heelSlabLoad(input: WallInput, g: RcCantileverGeometry, section: Section, cases: CaseParams[], dcs: DesignCase[]): { w: number; x: number; Msupport: number; Mspan: number; V: number; caseId: string } {
  const cf = counterfortGeometry(g)!;
  const f = rcFrame(g);
  let best = { w: -Infinity, x: f.xStemBack, caseId: "" };
  for (const dc of dcs) {
    const cp = cases.find((c) => c.id === dc.caseId)!;
    const isSeismic = cp.id !== "static";
    const kvF = isSeismic ? cp.kvFactor : 1, gF = isSeismic ? input.loads.selfWeightFactorSeismic : 1;
    const wSlab = input.material.gamma * f.tf * kvF * gF * dc.combo.G;
    for (let i = 0; i <= 20; i++) {
      const x = f.xStemBack + ((f.B - f.xStemBack) * i) / 20;
      const w = wSlab + soilColumnWeight(input, section, x, f.tf) * kvF * gF * dc.combo.G + input.loads.surcharge * kvF * dc.combo.Q - ((input.rc.heelUplift ? dc.qAt(x) : 0) + upliftAt(input, f.B, x) * dc.combo.F);
      if (w > best.w) best = { w, x, caseId: dc.id };
    }
  }
  const w = Math.max(0, best.w);
  return { w, x: best.x, Msupport: (w * cf.L * cf.L) / 12, Mspan: (w * cf.L * cf.L) / 16, V: (w * cf.L) / 2, caseId: best.caseId };
}

/** Payanda ağırlığının birim uzunluğa yayılmış net değeri (beton − yerini aldığı dolgu) ve ağırlık merkezi */
export function counterfortWeightPerMetre(input: WallInput, g: RcCantileverGeometry): { W: number; x: number; y: number } | null {
  const cf = counterfortGeometry(g);
  if (!cf) return null;
  const gammaSoil = input.backfill.layers[0]!.gamma;
  const W = ((input.material.gamma - gammaSoil) * cf.area * cf.t) / cf.s;
  return { W, x: cf.centroid.x, y: cf.centroid.y };
}

export const stemThicknessAvg = (g: RcCantileverGeometry): number => (g.stem.topThickness + g.stem.bottomThickness) / 2;
export const passiveAngle = deg2rad;
